import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Users, Shield, User as UserIcon, MessageCircle, RefreshCw, Play, CheckCircle2, XCircle, AlertTriangle, Loader2, Clock, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export const Settings = () => {
  const { user, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [sendingManual, setSendingManual] = useState(null);

  useEffect(() => {
    fetchUsers();
    runDiagnose();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Errore caricamento utenti');
    } finally {
      setLoading(false);
    }
  };

  const runDiagnose = async () => {
    setDiagLoading(true);
    try {
      const res = await api.get('/reminders/diagnose');
      setDiag(res.data);
    } catch { setDiag(null); }
    finally { setDiagLoading(false); }
  };

  const triggerCheck = async () => {
    setTriggerLoading(true);
    try {
      await api.post('/reminders/trigger');
      toast.success('Controllo reminder completato');
      runDiagnose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setTriggerLoading(false); }
  };

  const sendManual = async (appId, nome) => {
    setSendingManual(appId);
    try {
      const res = await api.post(`/reminders/send-manual/${appId}`);
      toast.success(res.data.message || `Reminder inviato a ${nome}`);
      runDiagnose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore invio'); }
    finally { setSendingManual(null); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 md:p-8 lg:p-10" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-2">
          Impostazioni
        </h1>
        <p className="text-base text-slate-600">
          Gestisci utenti e configurazioni sistema
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-2xl font-semibold text-slate-900 mb-6 flex items-center gap-2">
              <Users size={24} />
              Utenti Sistema
            </h2>

            <div className="space-y-3">
              {users.map(u => (
                <div
                  key={u.id}
                  data-testid={`user-item-${u.id}`}
                  className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-blue-300 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <img
                      src={u.avatar || `https://ui-avatars.com/api/?name=${u.nome}+${u.cognome}`}
                      alt={`${u.nome} ${u.cognome}`}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-semibold text-slate-900">
                        {u.nome} {u.cognome}
                      </p>
                      <p className="text-sm text-slate-600">{u.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <span
                      className={`
                        px-3 py-1 rounded-full text-xs font-medium
                        ${u.ruolo === 'super_admin'
                          ? 'bg-purple-50 text-purple-600 border border-purple-200'
                          : 'bg-blue-50 text-blue-600 border border-blue-200'
                        }
                      `}
                    >
                      {u.ruolo === 'super_admin' ? (
                        <span className="flex items-center gap-1">
                          <Shield size={12} />
                          Super Admin
                        </span>
                      ) : (
                        <span className="flex items-center gap-1">
                          <UserIcon size={12} />
                          Dipendente
                        </span>
                      )}
                    </span>

                    <div
                      className={`w-2 h-2 rounded-full ${
                        u.is_online ? 'bg-green-500' : 'bg-slate-300'
                      }`}
                      title={u.is_online ? 'Online' : 'Offline'}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Il Tuo Profilo</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <img
                  src={user?.avatar || `https://ui-avatars.com/api/?name=${user?.nome}`}
                  alt={user?.nome}
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div>
                  <p className="font-semibold text-slate-900">
                    {user?.nome} {user?.cognome}
                  </p>
                  <p className="text-sm text-slate-600">{user?.email}</p>
                </div>
              </div>
              
              <div className="pt-3 border-t border-slate-100">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Ruolo:</span>
                    <span className="font-medium text-slate-900">
                      {user?.ruolo === 'super_admin' ? 'Super Admin' : 'Dipendente'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Stato:</span>
                    <span className="font-medium text-green-600">Attivo</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Info Sistema</h3>
            <div className="space-y-2 text-sm text-blue-700">
              <p>Utenti totali: {users.length}</p>
              <p>Super Admin: {users.filter(u => u.ruolo === 'super_admin').length}</p>
              <p>Dipendenti: {users.filter(u => u.ruolo === 'dipendente').length}</p>
              <p>Online: {users.filter(u => u.is_online).length}</p>
            </div>
          </div>

          {/* ─── Diagnostica Reminder WhatsApp ─── */}
          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="reminder-diag">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <MessageCircle size={18} className="text-emerald-600" />
                Reminder WhatsApp
              </h3>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={runDiagnose} disabled={diagLoading} data-testid="diag-refresh">
                {diagLoading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              </Button>
            </div>

            {diag ? (
              <div className="space-y-3 text-xs">
                {/* Connessione */}
                <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                  <p className="font-semibold text-slate-700 mb-1">Connessione WhatsApp</p>
                  <div className="flex items-center gap-2">
                    {diag.whatsapp_connessione === 'OK' ? (
                      <><CheckCircle2 size={14} className="text-emerald-500" /><span className="text-emerald-700 font-medium">Connesso</span></>
                    ) : (
                      <><XCircle size={14} className="text-red-500" /><span className="text-red-700 font-medium">{diag.whatsapp_connessione}</span></>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1 break-all">URL: {diag.whatsapp_url}</p>
                </div>

                {/* Stato scheduler */}
                <div className="flex items-center justify-between p-2 rounded-lg bg-slate-50">
                  <span className="text-slate-700">Scheduler</span>
                  <Badge className={diag.scheduler_attivo ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                    {diag.scheduler_attivo ? 'Attivo' : 'Disattivo'}
                  </Badge>
                </div>

                {/* Ora e data */}
                <div className="flex items-center justify-between p-2 rounded-lg bg-slate-50">
                  <span className="text-slate-700">Ora server (Italia)</span>
                  <span className="font-mono font-medium">{diag.ora_italia}</span>
                </div>

                {/* Stats oggi */}
                <div className="flex gap-2">
                  <div className="flex-1 p-2 bg-emerald-50 rounded-lg text-center">
                    <p className="text-lg font-bold text-emerald-700">{diag.reminder_inviati_oggi}</p>
                    <p className="text-[10px] text-emerald-600">Inviati oggi</p>
                  </div>
                  <div className="flex-1 p-2 bg-red-50 rounded-lg text-center">
                    <p className="text-lg font-bold text-red-700">{diag.reminder_falliti_oggi}</p>
                    <p className="text-[10px] text-red-600">Falliti oggi</p>
                  </div>
                </div>

                {/* Appuntamenti oggi */}
                {diag.appuntamenti_oggi?.length > 0 && (
                  <div>
                    <p className="font-semibold text-slate-700 mb-1">Oggi ({diag.oggi})</p>
                    {diag.appuntamenti_oggi.map(a => (
                      <div key={a.id} className="flex items-center justify-between p-2 rounded-lg bg-blue-50 border border-blue-100 mb-1">
                        <div>
                          <p className="font-medium text-slate-800">{a.nome} <span className="text-slate-500">- {a.ora || '?'}</span></p>
                          <p className="text-[10px] text-slate-500">{a.telefono}</p>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {a.reminder_3h_inviato ? (
                            <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">Inviato</Badge>
                          ) : (
                            <Button size="sm" className="h-6 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white px-2"
                              onClick={() => sendManual(a.id, a.nome)} disabled={sendingManual === a.id} data-testid={`send-diag-${a.id}`}>
                              {sendingManual === a.id ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} className="mr-1" />}
                              Invia
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Appuntamenti domani */}
                {diag.appuntamenti_domani?.length > 0 && (
                  <div>
                    <p className="font-semibold text-slate-700 mb-1">Domani ({diag.domani})</p>
                    {diag.appuntamenti_domani.map(a => (
                      <div key={a.id} className="flex items-center justify-between p-2 rounded-lg bg-amber-50 border border-amber-100 mb-1">
                        <div>
                          <p className="font-medium text-slate-800">{a.nome} <span className="text-slate-500">- {a.ora || '?'}</span></p>
                          <p className="text-[10px] text-slate-500">{a.telefono}</p>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {a.reminder_1g_inviato ? (
                            <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">Inviato</Badge>
                          ) : (
                            <Button size="sm" className="h-6 text-[10px] bg-amber-600 hover:bg-amber-700 text-white px-2"
                              onClick={() => sendManual(a.id, a.nome)} disabled={sendingManual === a.id} data-testid={`send-diag-tomorrow-${a.id}`}>
                              {sendingManual === a.id ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} className="mr-1" />}
                              Invia
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {diag.appuntamenti_oggi?.length === 0 && diag.appuntamenti_domani?.length === 0 && (
                  <p className="text-slate-500 text-center py-2">Nessun appuntamento con telefono per oggi/domani</p>
                )}

                {/* Ultimi errori */}
                {diag.ultimi_errori?.length > 0 && (
                  <div>
                    <p className="font-semibold text-red-700 mb-1 flex items-center gap-1">
                      <AlertTriangle size={12} /> Ultimi errori
                    </p>
                    {diag.ultimi_errori.map((e, i) => (
                      <div key={i} className="p-2 rounded-lg bg-red-50 border border-red-100 mb-1">
                        <p className="font-medium text-red-800">{e.nome_cliente} ({e.telefono})</p>
                        <p className="text-[10px] text-red-600 break-all">{e.error_detail || 'Errore non specificato'}</p>
                        <p className="text-[9px] text-red-400">{e.sent_at}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Forza controllo */}
                {isAdmin && (
                  <Button className="w-full h-8 text-xs bg-slate-800 hover:bg-slate-900 text-white"
                    onClick={triggerCheck} disabled={triggerLoading} data-testid="force-check-btn">
                    {triggerLoading ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Play size={12} className="mr-1.5" />}
                    Forza controllo reminder adesso
                  </Button>
                )}
              </div>
            ) : diagLoading ? (
              <div className="text-center py-4"><Loader2 size={20} className="animate-spin mx-auto text-slate-400" /></div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">Diagnostica non disponibile</p>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};